import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CreditCard, Check, RefreshCw, Sparkles, Shield, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useMembership } from '../contexts/MembershipContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Payment() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { membership, refreshMembership } = useMembership();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    // Clean up stale body scroll/pointer lock from Radix Dialog
    useEffect(() => {
        document.body.style.pointerEvents = '';
        document.body.style.overflow = '';
    }, []);

    const plans = [
        {
            id: 'monthly',
            name: 'Monthly Access',
            price: 5,
            duration: '1 month',
            description: 'Perfect for short-term exam prep',
            features: ['Full question bank access', 'AI-generated questions', 'Performance analytics', 'Study planner'],
            popular: false,
            type: 'one-time'
        },
        {
            id: 'yearly',
            name: 'Yearly Access',
            price: 50,
            duration: '1 year',
            description: 'Best value for dedicated students',
            features: ['Full question bank access', 'AI-generated questions', 'Performance analytics', 'Study planner', 'Save $10 vs monthly!'],
            popular: true,
            type: 'one-time',
            savings: 10
        },
        {
            id: 'subscription',
            name: 'Monthly Subscription',
            price: 5,
            duration: 'per month',
            description: 'Flexible, cancel anytime',
            features: ['Full question bank access', 'AI-generated questions', 'Performance analytics', 'Study planner', 'Auto-renews monthly', 'Cancel anytime'],
            popular: false,
            type: 'subscription'
        }
    ];

    useEffect(() => {
        fetchSubscriptionStatus();
        
        // Handle return from PayPal
        const status = searchParams.get('status');
        if (status === 'success') {
            toast.success('Payment successful! Your access is now active.');
            refreshMembership();
        } else if (status === 'cancelled') {
            toast.info('Payment cancelled.');
        }
    }, [searchParams]);

    const fetchSubscriptionStatus = async () => {
        try {
            const token = localStorage.getItem('medprep_token');
            const response = await fetch(`${API_URL}/api/payments/subscription-status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubscriptionStatus(data);
            }
        } catch (err) {
            console.error('Failed to fetch subscription status:', err);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? Your access will remain active until the end of your current billing period.')) {
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('medprep_token');
            const response = await fetch(`${API_URL}/api/payments/cancel-subscription`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(data.message);
                fetchSubscriptionStatus();
                refreshMembership();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to cancel subscription');
            }
        } catch (err) {
            toast.error('Failed to cancel subscription');
        } finally {
            setLoading(false);
        }
    };

    const createOrder = async (planId) => {
        const token = localStorage.getItem('medprep_token');
        if (!token) {
            throw new Error('Your session has expired. Please log in again.');
        }
        const response = await fetch(`${API_URL}/api/payments/create-order`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ plan_type: planId })
        });

        if (!response.ok) {
            let errMsg = `Payment failed (HTTP ${response.status})`;
            try {
                const text = await response.text();
                try {
                    const parsed = JSON.parse(text);
                    errMsg = parsed.detail || errMsg;
                } catch {
                    if (text) errMsg = `Payment failed (${response.status}): ${text.substring(0, 200)}`;
                }
            } catch {}
            throw new Error(errMsg);
        }

        const data = await response.json();
        
        // For PayPal redirect flow, we need to redirect to approval URL
        if (data.approval_url) {
            setRedirecting(true);
            // Redirect immediately - the redirecting screen will show
            window.location.replace(data.approval_url);
        } else {
            throw new Error('No approval URL received from PayPal');
        }
        
        return data.payment_id;
    };

    const createSubscription = async () => {
        const token = localStorage.getItem('medprep_token');
        if (!token) {
            throw new Error('Your session has expired. Please log in again.');
        }
        const response = await fetch(`${API_URL}/api/payments/create-subscription`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            let errMsg = `Subscription failed (HTTP ${response.status})`;
            try {
                const text = await response.text();
                try {
                    const parsed = JSON.parse(text);
                    errMsg = parsed.detail || errMsg;
                } catch {
                    if (text) errMsg = `Subscription failed (${response.status}): ${text.substring(0, 200)}`;
                }
            } catch {}
            throw new Error(errMsg);
        }

        const data = await response.json();
        
        if (data.approval_url) {
            setRedirecting(true);
            window.location.replace(data.approval_url);
        } else {
            throw new Error('No approval URL received from PayPal');
        }
        
        return data.agreement_id;
    };

    const handlePlanSelect = async (plan) => {
        setSelectedPlan(plan.id);
        setLoading(true);

        try {
            if (plan.type === 'subscription') {
                await createSubscription();
            } else {
                await createOrder(plan.id);
            }
        } catch (err) {
            console.error('Payment error:', err);
            let msg = 'Failed to initiate payment. Please try again.';
            try {
                if (err && typeof err.message === 'string') {
                    msg = err.message === 'Failed to fetch'
                        ? 'Unable to connect to payment server. Please check your internet connection and try again.'
                        : err.message;
                }
            } catch {}
            toast.error(String(msg));
            setLoading(false);
            setRedirecting(false);
        }
    };

    // Show redirecting screen
    if (redirecting) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
                <Card className="w-full max-w-md mx-4">
                    <CardContent className="pt-8 pb-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 animate-pulse">
                            <ExternalLink className="h-8 w-8 text-blue-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">Redirecting to PayPal</h2>
                        <p className="text-slate-600 mb-4">Please wait while we connect you to PayPal's secure checkout...</p>
                        <div className="flex items-center justify-center gap-2">
                            <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                            <span className="text-sm text-slate-500">This may take a few seconds</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show current subscription management if user has active subscription
    if (subscriptionStatus?.has_subscription) {
        const isCancelled = subscriptionStatus.cancelled_at || !subscriptionStatus.auto_renew;
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
                <div className="max-w-2xl mx-auto">
                    <Card className={isCancelled ? "border-amber-200 bg-amber-50/50" : "border-emerald-200 bg-emerald-50/50"}>
                        <CardHeader className="text-center">
                            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isCancelled ? "bg-amber-100" : "bg-emerald-100"}`}>
                                <Check className={`h-8 w-8 ${isCancelled ? "text-amber-600" : "text-emerald-600"}`} />
                            </div>
                            <CardTitle className={`text-2xl ${isCancelled ? "text-amber-800" : "text-emerald-800"}`}>
                                {isCancelled ? "Subscription Cancelled" : "Active Subscription"}
                            </CardTitle>
                            <CardDescription className={isCancelled ? "text-amber-600" : "text-emerald-600"}>
                                {isCancelled
                                    ? "Your subscription has been cancelled. Access remains active until your billing period ends."
                                    : "Your monthly subscription is active and will auto-renew"}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="bg-white rounded-lg p-6 space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">Plan</span>
                                    <span className="font-medium">Monthly Subscription</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">Amount</span>
                                    <span className="font-medium">$5.00/month</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">{isCancelled ? "Access until" : "Next billing date"}</span>
                                    <span className="font-medium">
                                        {subscriptionStatus.expires_at 
                                            ? new Date(subscriptionStatus.expires_at).toLocaleDateString()
                                            : 'N/A'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-600">Status</span>
                                    <Badge className={isCancelled ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                                        {isCancelled ? "Cancelled" : "Active"}
                                    </Badge>
                                </div>
                                {isCancelled && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-600">Cancelled on</span>
                                        <span className="font-medium text-amber-700">
                                            {subscriptionStatus.cancelled_at 
                                                ? new Date(subscriptionStatus.cancelled_at).toLocaleDateString()
                                                : 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/dashboard')}
                                    className="w-full"
                                    data-testid="back-to-dashboard-btn"
                                >
                                    Back to Dashboard
                                </Button>
                                {!isCancelled && (
                                    <Button
                                        variant="ghost"
                                        onClick={handleCancelSubscription}
                                        disabled={loading}
                                        className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid="cancel-subscription-btn"
                                    >
                                        {loading ? 'Cancelling...' : 'Cancel Subscription'}
                                    </Button>
                                )}
                                {isCancelled && (
                                    <p className="text-xs text-center text-amber-600 font-medium">
                                        Want to resubscribe? Your current access will remain active until the date above, then you can choose a new plan.
                                    </p>
                                )}
                            </div>

                            {!isCancelled && (
                                <p className="text-xs text-center text-slate-500">
                                    If you cancel, your access will remain active until the end of your current billing period.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                        Upgrade to Premium
                    </h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        Get unlimited access to our AI-powered question bank and ace your NBME & CBSE exams
                    </p>
                </div>

                {/* Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                            <Sparkles className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-slate-900">AI-Powered Questions</h3>
                            <p className="text-sm text-slate-600">Unlimited practice with intelligent question generation</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <Shield className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-slate-900">Exam-Style Format</h3>
                            <p className="text-sm text-slate-600">Questions mirror actual NBME & CBSE format</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-medium text-slate-900">Smart Study Planner</h3>
                            <p className="text-sm text-slate-600">Personalized study schedules and progress tracking</p>
                        </div>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {plans.map((plan) => (
                        <Card 
                            key={plan.id}
                            className={`relative cursor-pointer transition-all hover:shadow-lg ${
                                plan.popular 
                                    ? 'border-indigo-500 border-2 shadow-lg' 
                                    : 'border-slate-200 hover:border-indigo-300'
                            } ${selectedPlan === plan.id ? 'ring-2 ring-indigo-500' : ''}`}
                            onClick={() => !loading && handlePlanSelect(plan)}
                            data-testid={`plan-card-${plan.id}`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <Badge className="bg-indigo-600 text-white px-3 py-1">
                                        Best Value
                                    </Badge>
                                </div>
                            )}
                            <CardHeader className="text-center pt-8">
                                <CardTitle className="text-xl">{plan.name}</CardTitle>
                                <div className="mt-4">
                                    <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                                    <span className="text-slate-600 ml-1">/{plan.duration}</span>
                                </div>
                                {plan.savings && (
                                    <Badge variant="secondary" className="mt-2 bg-emerald-100 text-emerald-700">
                                        Save ${plan.savings}
                                    </Badge>
                                )}
                                <CardDescription className="mt-2">{plan.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3 mb-6">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-sm">
                                            <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                            <span className="text-slate-600">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    className={`w-full ${
                                        plan.popular 
                                            ? 'bg-indigo-600 hover:bg-indigo-700' 
                                            : ''
                                    }`}
                                    variant={plan.popular ? 'default' : 'outline'}
                                    disabled={loading && selectedPlan === plan.id}
                                    data-testid={`plan-btn-${plan.id}`}
                                >
                                    {loading && selectedPlan === plan.id ? (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <CreditCard className="mr-2 h-4 w-4" />
                                            {plan.type === 'subscription' ? 'Subscribe' : 'Buy Now'}
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Trust badges */}
                <div className="text-center">
                    <p className="text-sm text-slate-500 mb-4">Secure payment powered by PayPal</p>
                    <div className="flex items-center justify-center gap-6">
                        <img 
                            src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" 
                            alt="PayPal" 
                            className="h-6 opacity-60"
                        />
                    </div>
                </div>

                {/* Back link */}
                <div className="text-center mt-8">
                    <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                        ← Back to Dashboard
                    </Button>
                </div>
            </div>
        </div>
    );
}
